import React, { useMemo, useState } from "react";
import Swal from "sweetalert2";
import { API_BASE } from "../config";
import { apiFetch } from "../utilities/apiFetch";
import { leerArchivoBanco, codificacionDe, FUENTES } from "../utilities/archivosBanco";
import { analizarBancos, bdDeOficial, TIPOS_CASO, ETIQUETAS_FUENTE } from "../utilities/conciliacionBancaria";
import { casosPublicables, casoParaPublicar, casosAConfirmar, identidadDeCaso, fechaLimite, ESTADOS_ABIERTOS } from "../utilities/casosPlantel";

// Modulo "Conciliacion" -> pestaña "Bancos" (solo Administrador). Financieros sube los archivos de los bancos tal
// como los descarga; los movimientos se guardan en la BD (sin duplicarse) y se cruzan contra lo registrado en
// Multipagos. Muestra por plantel cuanto cobro el banco, cuanto se deposito y cuanto se registro, y los casos que
// cada plantel debe corregir. NO modifica ninguna orden. La logica esta en utilities/archivosBanco.js y
// utilities/conciliacionBancaria.js.

const hoyISO = () => {
  const h = new Date();
  return `${h.getFullYear()}-${String(h.getMonth() + 1).padStart(2, "0")}-${String(h.getDate()).padStart(2, "0")}`;
};
const primerDiaDelMes = () => `${hoyISO().substring(0, 8)}01`;
const dinero = (n) => `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fechaCorta = (iso) => (iso ? `${iso.substring(8, 10)}/${iso.substring(5, 7)}/${iso.substring(0, 4)}` : "");
const FILAS_VISIBLES = 100;

const leerTexto = (archivo) => new Promise((resolver, rechazar) => {
  const lector = new FileReader();
  lector.onload = () => resolver(lector.result);
  lector.onerror = () => rechazar(new Error("No se pudo leer el archivo."));
  lector.readAsText(archivo, codificacionDe(archivo.name));
});

const estiloTarjeta = { border: "1px solid #ddd", borderRadius: "8px", padding: "14px", backgroundColor: "#fff", boxShadow: "0 2px 4px rgba(0,0,0,0.05)" };
const estiloInput = { padding: "10px", border: "1px solid #ccc", borderRadius: "4px", fontSize: "1rem" };
const estiloTh = { textAlign: "left", padding: "6px 8px", borderBottom: "2px solid #ddd", fontSize: "0.8rem", whiteSpace: "nowrap" };
const estiloTd = { padding: "6px 8px", borderBottom: "1px solid #eee", fontSize: "0.85rem", verticalAlign: "top" };
const estiloNum = { ...estiloTd, textAlign: "right", whiteSpace: "nowrap" };
const estiloBotonSecundario = { border: "1px solid #003b5c", padding: "8px 14px", backgroundColor: "#fff", color: "#003b5c" };

const Tarjeta = ({ titulo, valor, detalle, color }) => (
  <div style={{ ...estiloTarjeta, borderLeft: `5px solid ${color}` }}>
    <div style={{ fontSize: "0.8rem", color: "#555" }}>{titulo}</div>
    <div style={{ fontSize: "1.4rem", fontWeight: "bold", color: "#222" }}>{valor}</div>
    {detalle && <div style={{ fontSize: "0.8rem", color: "#666" }}>{detalle}</div>}
  </div>
);

const Aviso = ({ children, tipo = "info" }) => {
  const colores = { error: ["#fdecea", "#f5c6cb", "#7a1c1c"], ok: ["#e8f5e9", "#c8e6c9", "#1b5e20"], info: ["#fff8e1", "#ffe082", "#7a5c00"] }[tipo];
  return <div style={{ margin: "8px 0", padding: "10px", borderRadius: "4px", fontSize: "0.85rem", backgroundColor: colores[0], border: `1px solid ${colores[1]}`, color: colores[2] }}>{children}</div>;
};

const ENCABEZADOS_CASOS = ["Plantel", "Nombre del plantel", "Tipo de caso", "Certeza", "Quién corrige", "Fuente", "Fecha", "Hora", "Importe",
  "Autorización (banco)", "Referencia (banco)", "Tecleado en la TPV", "Folio (banco)", "Orden", "Folio de la orden", "Estatus de la orden",
  "Método validado", "Capturado al validar", "Importe de la orden", "Validada", "Cajero", "Matrícula", "Detalle", "Qué hacer"];
const filaCaso = (c) => [c.oficial, c.plantel, TIPOS_CASO[c.tipo].titulo, c.probable ? "Probable" : "Segura", TIPOS_CASO[c.tipo].quien === "PLANTEL" ? "Plantel" : "Sistemas",
  ETIQUETAS_FUENTE[c.fuente] || c.fuente, c.fecha, c.hora, c.importe, c.autorizacion, c.referencia, c.tecleado, c.folioBanco, c.orden, c.folio, c.estatus,
  c.metodo, c.capturado, c.montoOrden, c.validada, c.cajero, c.matricula, c.nota, c.accion];
const ANCHOS_CASOS = [7, 26, 30, 9, 10, 18, 11, 6, 10, 12, 36, 22, 24, 8, 9, 10, 16, 22, 10, 16, 26, 16, 50, 70];

const VistaConciliacionBancaria = ({ catalogoPlanteles = [] }) => {
  const [archivos, setArchivos] = useState([]); // { nombre, lectura | error, guardado }
  const [guardando, setGuardando] = useState(false);
  const [fechaInicio, setFechaInicio] = useState(primerDiaDelMes());
  const [fechaFin, setFechaFin] = useState(hoyISO());
  const [consultando, setConsultando] = useState(false);
  const [error, setError] = useState("");
  const [resultado, setResultado] = useState(null);
  const [filtroPlantel, setFiltroPlantel] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [plantelPublicar, setPlantelPublicar] = useState("");
  const [publicando, setPublicando] = useState(false);

  // oficial ("07") -> nombre; el id de plantel en la BD va corrido en 2 respecto al oficial
  const nombresPlanteles = useMemo(() => {
    const nombres = {};
    catalogoPlanteles.forEach((p) => {
      const oficial = Number(p.id) - 2;
      if (oficial > 0) nombres[String(oficial).padStart(2, "0")] = String(p.nombre_plantel || "").trim();
    });
    return nombres;
  }, [catalogoPlanteles]);

  // ------------------------------------------------------------ 1. cargar archivos
  const elegirArchivos = async (lista) => {
    setResultado(null);
    const leidos = await Promise.all([...lista].map(async (archivo) => {
      try {
        return { nombre: archivo.name, lectura: leerArchivoBanco(archivo.name, await leerTexto(archivo)) };
      } catch (e) {
        return { nombre: archivo.name, error: e && e.message ? e.message : "No se pudo leer el archivo." };
      }
    }));
    setArchivos(leidos);
  };

  const porGuardar = archivos.filter((a) => a.lectura && !a.guardado);

  const guardar = async () => {
    setGuardando(true);
    let algunoGuardado = false;
    const nuevos = [...archivos];
    for (let i = 0; i < nuevos.length; i++) {
      const a = nuevos[i];
      if (!a.lectura || a.guardado) continue;
      try {
        const res = await apiFetch(`${API_BASE}/wMultipagosBancoGuardar.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fuente: a.lectura.fuente, nombre_archivo: a.nombre, fecha_desde: a.lectura.resumen.desde, fecha_hasta: a.lectura.resumen.hasta,
            omitidos: a.lectura.omitidos.length,
            movimientos: a.lectura.movimientos.map(({ oficial, ...m }) => ({ ...m, id_plantel: bdDeOficial(oficial) })),
          }),
        });
        const json = await res.json();
        nuevos[i] = json.success ? { ...a, guardado: json } : { ...a, errorGuardar: json.message || "El servidor no guardó el archivo." };
        if (json.success) algunoGuardado = true;
      } catch (e) {
        nuevos[i] = { ...a, errorGuardar: "No hubo respuesta del servidor. Intenta de nuevo." };
      }
      setArchivos([...nuevos]);
    }
    setGuardando(false);
    // Lo recien guardado se revisa de una vez con las fechas elegidas, y lo que coincide exactamente se valida con el banco
    if (algunoGuardado && !rangoInvalido) await consultar({ validar: true });
  };

  // ------------------------------------------------------------ 2. consultar un periodo
  const rangoInvalido = !fechaInicio || !fechaFin || fechaInicio > fechaFin;
  // validar: despues de cruzar, valida con el banco lo que coincide exactamente y vuelve a cruzar.
  // validacion: resultado de una validacion ya hecha, para mostrarlo.
  const consultar = async ({ validar = false, validacion = null } = {}) => {
    setError("");
    setResultado(null);
    setConsultando(true);
    try {
      const res = await apiFetch(`${API_BASE}/wMultipagosBancoConsultar.php?${new URLSearchParams({ fecha_inicio: fechaInicio, fecha_fin: fechaFin })}`);
      const json = await res.json();
      if (!json.success) { setError(json.message || "No se pudo hacer la consulta."); return; }
      const d = json.data;
      const cobertura = {};
      d.cobertura.forEach((c) => { cobertura[c.fuente] = { desde: c.desde, hasta: c.hasta, movimientos: Number(c.movimientos) }; });
      const cajeros = {};
      d.cajeros.forEach((c) => { cajeros[String(c.id)] = { nombre: String(c.nombre_cajero || "").trim(), id_plantel: String(c.id_plantel) }; });
      const periodo = { inicio: fechaInicio, fin: fechaFin };
      const analisis = analizarBancos({ movimientos: d.movimientos, ordenes: d.ordenes, cajeros, cobertura, periodo, nombresPlanteles });
      const pendientesDeValidar = analisis.validarConBanco.validar.length + analisis.validarConBanco.confirmar.length;
      if (validar && pendientesDeValidar) {
        const hecha = await enviarValidacion(analisis.validarConBanco);
        await consultar({ validacion: hecha });
        return;
      }
      const seguimiento = Object.keys(cobertura).length ? await sincronizarCasos(analisis.casos, periodo) : { identidades: new Set(), confirmados: 0 };
      setFiltroPlantel("");
      setFiltroTipo("");
      setPlantelPublicar("");
      setResultado({ ...analisis, cobertura, cargas: d.cargas, periodo, ahora: json.ahora, seguimiento, validacion });
    } catch (e) {
      console.error("Error al consultar la conciliacion bancaria:", e);
      setError("Ocurrió un problema al consultar. Intenta de nuevo.");
    } finally {
      setConsultando(false);
    }
  };

  // ------------------------------------------------------------ validacion con el banco
  // El servidor vuelve a revisar cada coincidencia; regresa cuantas valido, cuantas confirmo y cuales omitio y por que
  const enviarValidacion = async ({ validar, confirmar }) => {
    try {
      const res = await apiFetch(`${API_BASE}/wMultipagosBancoValidar.php`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ validar: validar.map(({ clave, orden }) => ({ clave, orden })), confirmar }),
      });
      const json = await res.json();
      return json.success ? json : { error: json.message || "El servidor no validó los pagos." };
    } catch (e) {
      return { error: "No hubo respuesta del servidor al validar con el banco." };
    }
  };
  const validarAhora = async () => {
    setConsultando(true);
    const hecha = await enviarValidacion(resultado.validarConBanco);
    await consultar({ validacion: hecha });
  };

  // ------------------------------------------------------------ 3. casos publicados a los planteles
  // Los casos abiertos que el cruce de hoy ya no marca se confirman solos (el plantel ya los corrigio o se
  // resolvieron de otra forma). Regresa las identidades que siguen abiertas, para no publicarlas otra vez.
  const sincronizarCasos = async (casosActuales, periodo) => {
    try {
      const res = await apiFetch(`${API_BASE}/wMultipagosCasosSeguimiento.php`);
      const json = await res.json();
      if (!json.success) return { error: json.message || "No se pudo consultar el seguimiento de los casos.", identidades: new Set(), confirmados: 0 };
      let abiertos = json.data.casos.filter((k) => ESTADOS_ABIERTOS.includes(k.estado));
      const ids = casosAConfirmar(abiertos, casosActuales, periodo);
      let confirmados = 0;
      if (ids.length) {
        const r = await apiFetch(`${API_BASE}/wMultipagosCasosCerrar.php`, {
          method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ids, estado: "CONFIRMADO" }),
        });
        const j = await r.json();
        if (j.success) {
          confirmados = Number(j.actualizados) || 0;
          abiertos = abiertos.filter((k) => !ids.includes(Number(k.id)));
        }
      }
      return { identidades: new Set(abiertos.map((k) => k.identidad)), confirmados, abiertos: abiertos.length };
    } catch (e) {
      return { error: "No se pudo consultar el seguimiento de los casos.", identidades: new Set(), confirmados: 0 };
    }
  };

  const publicar = async (lista) => {
    const planteles = new Set(lista.map((c) => c.oficial)).size;
    const limite = fechaLimite(hoyISO());
    const decision = await Swal.fire({
      title: "Publicar casos a los planteles",
      text: `Se publicarán ${lista.length} caso(s) a ${planteles} plantel(es). Los cajeros los verán en "Casos por corregir" y tendrán hasta el ${fechaCorta(limite)} (5 días hábiles).`,
      icon: "question", showCancelButton: true, confirmButtonText: "Publicar", cancelButtonText: "Cancelar",
    });
    if (!decision.isConfirmed) return;
    setPublicando(true);
    try {
      const res = await apiFetch(`${API_BASE}/wMultipagosCasosPublicar.php`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ casos: lista.map(casoParaPublicar) }),
      });
      const json = await res.json();
      if (!json.success) { Swal.fire("No se publicó", json.message || "El servidor no publicó los casos.", "error"); return; }
      const identidades = new Set([...resultado.seguimiento.identidades, ...lista.map(identidadDeCaso)]);
      setResultado({ ...resultado, seguimiento: { ...resultado.seguimiento, identidades, error: "" } });
      Swal.fire("Casos publicados", `${json.publicados} publicado(s)${json.ya_abiertos ? `, ${json.ya_abiertos} ya estaban abiertos` : ""}. Vencen el ${fechaCorta(json.fecha_limite)}.`, "success");
    } catch (e) {
      Swal.fire("Error", "No hubo respuesta del servidor. Intenta de nuevo.", "error");
    } finally {
      setPublicando(false);
    }
  };

  // ------------------------------------------------------------ descargas
  const armarLibro = async (hojas) => {
    const XLSX = await import("xlsx");
    const libro = XLSX.utils.book_new();
    hojas.forEach(({ nombre, encabezados, datos, anchos }) => {
      const hoja = XLSX.utils.aoa_to_sheet([encabezados, ...datos]);
      hoja["!cols"] = anchos.map((w) => ({ wch: w }));
      XLSX.utils.book_append_sheet(libro, hoja, nombre);
    });
    return { XLSX, libro };
  };
  const nombreArchivo = (texto) => String(texto || "").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^A-Za-z0-9]+/g, "_").replace(/^_|_$/g, "");

  const descargarGeneral = async () => {
    try {
      const r = resultado;
      const resumen = r.planteles.map((p) => [p.oficial, p.nombre, p.banco.TARJETA.cobrado, p.banco.BANORTE.cobrado, p.banco.BBVA.cobrado, p.cobrado,
        p.comisiones, p.depositado, p.registrado, p.diferencia, p.casos]);
      const { XLSX, libro } = await armarLibro([
        { nombre: "Por plantel", encabezados: ["Plantel", "Nombre", "Cobrado tarjeta", "Cobrado Banorte", "Cobrado BBVA", "Cobrado total", "Comisiones e IVA", "Depositado", "Registrado en Multipagos", "Diferencia", "Casos"],
          datos: resumen, anchos: [7, 30, 14, 14, 14, 14, 14, 14, 18, 12, 7] },
        { nombre: "Casos", encabezados: ENCABEZADOS_CASOS, datos: r.casos.map(filaCaso), anchos: ANCHOS_CASOS },
      ]);
      XLSX.writeFile(libro, `Conciliacion_bancos_${r.periodo.inicio}_a_${r.periodo.fin}.xlsx`);
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "No se pudo generar el archivo de Excel.", "error");
    }
  };

  const descargarPlantel = async (oficial) => {
    try {
      const r = resultado;
      const casos = r.casos.filter((c) => c.oficial === oficial && TIPOS_CASO[c.tipo].quien === "PLANTEL");
      const { XLSX, libro } = await armarLibro([{ nombre: "Casos", encabezados: ENCABEZADOS_CASOS, datos: casos.map(filaCaso), anchos: ANCHOS_CASOS }]);
      XLSX.writeFile(libro, `Conciliacion_plantel_${oficial}_${nombreArchivo(nombresPlanteles[oficial] || "plantel")}_${r.periodo.inicio}_a_${r.periodo.fin}.xlsx`);
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "No se pudo generar el archivo de Excel.", "error");
    }
  };

  // ------------------------------------------------------------ presentacion
  const r = resultado;
  const casosVisibles = useMemo(() => (r ? r.casos.filter((c) => (!filtroPlantel || c.oficial === filtroPlantel) && (!filtroTipo || c.tipo === filtroTipo)) : []), [r, filtroPlantel, filtroTipo]);
  const tiposPresentes = r ? Object.keys(TIPOS_CASO).filter((t) => r.casos.some((c) => c.tipo === t)) : [];
  const yaPublicado = (c) => r && r.seguimiento && r.seguimiento.identidades.has(identidadDeCaso(c));
  const porPublicar = r ? casosPublicables(r.casos, plantelPublicar).filter((c) => !yaPublicado(c)) : [];
  const plantelesConNuevos = r ? r.planteles.filter((p) => p.oficial && casosPublicables(r.casos, p.oficial).some((c) => !yaPublicado(c))) : [];
  const avisosCobertura = r ? Object.keys(FUENTES).map((f) => {
    const c = r.cobertura[f];
    if (!c) return `${FUENTES[f]}: no hay nada cargado. Sube el archivo para revisar esa parte.`;
    if (c.hasta < r.periodo.fin) return `${FUENTES[f]}: lo cargado llega hasta el ${fechaCorta(c.hasta)}. Lo posterior todavía no se revisa.`;
    return "";
  }).filter(Boolean) : [];

  return (
    <div className="Dashboard-card">
      <h3 style={{ marginBottom: "5px", color: "#003b5c" }}>Conciliación con los bancos</h3>
      <p style={{ color: "#666", fontSize: "0.9rem", marginTop: 0 }}>
        Compara lo que dicen los bancos (cobrado, comisiones y depositado) contra lo que cada plantel registró en Multipagos, y
        lista los casos que cada plantel debe corregir. Al guardar los archivos, los pagos que coinciden <b>exactamente</b> con una orden
        pendiente se validan con el banco (y quedan en la bitácora); todo lo demás no cambia ninguna orden.
      </p>

      {/* ---------------- 1. cargar ---------------- */}
      <h4 className="titulo-seccion">1. Cargar archivos del banco</h4>
      <ol style={{ fontSize: "0.85rem", color: "#555", paddingLeft: "20px", margin: "0 0 10px 0" }}>
        <li><b>Tarjeta (TPV y en línea):</b> MultiCobros → Analítica → Reportes → <b>Reporte de depósitos</b>, grupo TodasLasAfiliaciones, exportado a CSV.</li>
        <li><b>Ventanilla Banorte:</b> portal de Banorte, pagos del servicio 05446, exportado a CSV.</li>
        <li><b>Ventanilla BBVA:</b> estado de cuenta en formato Cuaderno 43 (archivo <code>.exp</code>).</li>
        <li>Súbelos <b>tal como se descargan</b> (sin abrirlos ni guardarlos en Excel). Si subes un periodo que ya estaba, no se duplica nada.</li>
      </ol>
      <div style={{ padding: "15px", backgroundColor: "#f8f9fa", borderRadius: "6px", margin: "10px 0" }}>
        <label htmlFor="banco-archivos" style={{ display: "block", fontWeight: "bold", marginBottom: "6px", fontSize: "0.85rem" }}>Archivos del banco (puedes elegir varios)</label>
        <input id="banco-archivos" type="file" multiple accept=".csv,.exp" onChange={(e) => e.target.files && e.target.files.length && elegirArchivos(e.target.files)} style={{ fontSize: "0.9rem" }} />
      </div>

      {archivos.length > 0 && (
        <div style={{ overflowX: "auto", marginBottom: "10px" }}>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead><tr>{["Archivo", "Tipo", "Movimientos", "Fechas", "Cobrado", "Depositado", "Estado"].map((t) => <th key={t} style={estiloTh}>{t}</th>)}</tr></thead>
            <tbody>
              {archivos.map((a) => (
                <tr key={a.nombre}>
                  <td style={estiloTd}>{a.nombre}</td>
                  {a.error ? (
                    <td style={{ ...estiloTd, color: "#7a1c1c" }} colSpan={6}>{a.error}</td>
                  ) : (
                    <>
                      <td style={estiloTd}>{FUENTES[a.lectura.fuente]}</td>
                      <td style={estiloNum}>{a.lectura.resumen.movimientos}</td>
                      <td style={estiloTd}>{fechaCorta(a.lectura.resumen.desde)} a {fechaCorta(a.lectura.resumen.hasta)}</td>
                      <td style={estiloNum}>{dinero(a.lectura.resumen.importe)}</td>
                      <td style={estiloNum}>{dinero(a.lectura.resumen.depositado)}</td>
                      <td style={estiloTd}>
                        {a.guardado && <span style={{ color: "#1b5e20" }}>Guardado: {a.guardado.nuevos} nuevos, {a.guardado.repetidos} ya estaban{a.guardado.actualizados ? `, ${a.guardado.actualizados} actualizados` : ""}</span>}
                        {a.errorGuardar && <span style={{ color: "#7a1c1c" }}>{a.errorGuardar}</span>}
                        {!a.guardado && !a.errorGuardar && "Listo para guardar"}
                        {a.lectura.omitidos.length > 0 && <div style={{ fontSize: "0.75rem", color: "#666" }}>No son cobros y no se guardan: {a.lectura.omitidos.join("; ")}</div>}
                        {a.lectura.resumen.sinPlantel > 0 && <div style={{ fontSize: "0.75rem", color: "#7a5c00" }}>{a.lectura.resumen.sinPlantel} sin plantel identificado</div>}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={guardar} disabled={!porGuardar.length || guardando} className="btn-select validar"
            style={{ border: "none", padding: "10px 18px", marginTop: "10px", opacity: (!porGuardar.length || guardando) ? 0.45 : 1, cursor: (!porGuardar.length || guardando) ? "not-allowed" : "pointer" }}>
            {guardando ? "Guardando..." : `Guardar en el sistema${porGuardar.length ? ` (${porGuardar.length})` : ""}`}
          </button>
        </div>
      )}

      {/* ---------------- 2. consultar ---------------- */}
      <h4 className="titulo-seccion" style={{ marginTop: "20px" }}>2. Revisar un periodo</h4>
      <div style={{ display: "flex", gap: "15px", alignItems: "flex-end", flexWrap: "wrap", padding: "15px", backgroundColor: "#f8f9fa", borderRadius: "6px", margin: "10px 0" }}>
        <div>
          <label htmlFor="banco-desde" style={{ display: "block", fontWeight: "bold", marginBottom: "6px", fontSize: "0.85rem" }}>Desde</label>
          <input id="banco-desde" type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} style={estiloInput} />
        </div>
        <div>
          <label htmlFor="banco-hasta" style={{ display: "block", fontWeight: "bold", marginBottom: "6px", fontSize: "0.85rem" }}>Hasta</label>
          <input id="banco-hasta" type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} style={estiloInput} />
        </div>
        <button onClick={consultar} disabled={rangoInvalido || consultando} className="btn-select validar"
          style={{ border: "none", padding: "10px 18px", opacity: (rangoInvalido || consultando) ? 0.45 : 1, cursor: (rangoInvalido || consultando) ? "not-allowed" : "pointer" }}>
          {consultando ? "Revisando..." : "Revisar"}
        </button>
      </div>
      {rangoInvalido && <Aviso>La fecha inicial no puede ser mayor que la final.</Aviso>}
      {porGuardar.length > 0 && !guardando && (
        <Aviso>Tienes {porGuardar.length} archivo(s) sin guardar. Pulsa <b>Guardar en el sistema</b>: lo que no se guarda no entra en la revisión.</Aviso>
      )}
      {error && <Aviso tipo="error">{error}</Aviso>}

      {r && !Object.keys(r.cobertura).length && (
        <Aviso>Todavía no hay movimientos del banco guardados. Elige los archivos en el paso 1 y pulsa <b>Guardar en el sistema</b>.</Aviso>
      )}
      {r && Object.keys(r.cobertura).length > 0 && (
        <div>
          <p style={{ fontSize: "0.85rem", color: "#555" }}>
            Periodo {fechaCorta(r.periodo.inicio)} a {fechaCorta(r.periodo.fin)}. Cargado del banco:{" "}
            {Object.keys(FUENTES).map((f) => (r.cobertura[f] ? `${FUENTES[f]} del ${fechaCorta(r.cobertura[f].desde)} al ${fechaCorta(r.cobertura[f].hasta)}` : `${FUENTES[f]} sin datos`)).join("; ")}.
          </p>
          {avisosCobertura.map((a) => <Aviso key={a}>{a}</Aviso>)}
          {r.totales.porConfirmar > 0 && <Aviso>{r.totales.porConfirmar} validación(es) todavía no se pueden juzgar: son del último día cargado del banco (o posteriores), o de órdenes anteriores a lo cargado. Se revisarán al subir el siguiente archivo.</Aviso>}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "12px", margin: "14px 0" }}>
            <Tarjeta titulo="Cobrado por los bancos" valor={dinero(r.totales.cobrado)} detalle="lo que pagaron los alumnos" color="#003b5c" />
            <Tarjeta titulo="Comisiones e IVA" valor={dinero(r.totales.comisiones)} detalle={r.totales.comisionesBbva ? `más ${dinero(r.totales.comisionesBbva)} que BBVA cobra aparte` : "descontadas de cada venta con tarjeta"} color="#6a1b9a" />
            <Tarjeta titulo="Depositado a la cuenta" valor={dinero(r.totales.depositado)} detalle="cobrado menos comisiones" color="#2e7d32" />
            <Tarjeta titulo="Registrado en Multipagos" valor={dinero(r.totales.registrado)} detalle={r.totales.porConfirmarImporte ? `más ${dinero(r.totales.porConfirmarImporte)} validado después de lo cargado` : "con la fecha del cobro, hasta lo cargado del banco"} color="#ef6c00" />
            <Tarjeta titulo="Casos por corregir" valor={r.totales.casos} detalle={`${r.totales.ok} cobros cuadran`} color="#c62828" />
          </div>
          {(r.totales.pinpad.movimientos > 0 || r.totales.devoluciones > 0) && (
            <p style={{ fontSize: "0.8rem", color: "#666" }}>
              {r.totales.pinpad.movimientos > 0 && `No se suman: ${r.totales.pinpad.movimientos} cobro(s) de la Pinpad de pruebas (${dinero(r.totales.pinpad.importe)}). `}
              {r.totales.devoluciones > 0 && `${r.totales.devoluciones} devolución(es) con tarjeta (restan del cobrado).`}
            </p>
          )}
          <p style={{ fontSize: "0.8rem", color: "#666" }}>
            “Diferencia” = cobrado por el banco menos lo registrado. Lo registrado se cuenta con la fecha del cobro y solo hasta el último
            día cargado de cada banco, para comparar lo mismo; lo validado después queda en “Por confirmar” hasta que se suba el siguiente
            archivo. Si la diferencia no es cero, los casos dicen qué falta.
          </p>

          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", margin: "10px 0" }}>
            <button onClick={descargarGeneral} className="btn-select" style={estiloBotonSecundario}>Descargar Excel general</button>
          </div>

          <div style={{ overflowX: "auto", marginBottom: "18px" }}>
            <table style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead><tr>{["Plantel", "Tarjeta", "Banorte", "BBVA", "Comisiones", "Depositado", "Registrado", "Diferencia", "Por confirmar", "Casos", ""].map((t) => <th key={t} style={estiloTh}>{t}</th>)}</tr></thead>
              <tbody>
                {r.planteles.map((p) => (
                  <tr key={p.oficial || "sin"}>
                    <td style={estiloTd}>{p.oficial} {p.nombre}</td>
                    <td style={estiloNum}>{dinero(p.banco.TARJETA.cobrado)}</td>
                    <td style={estiloNum}>{dinero(p.banco.BANORTE.cobrado)}</td>
                    <td style={estiloNum}>{dinero(p.banco.BBVA.cobrado)}</td>
                    <td style={estiloNum}>{dinero(p.comisiones)}</td>
                    <td style={estiloNum}>{dinero(p.depositado)}</td>
                    <td style={estiloNum}>{dinero(p.registrado)}</td>
                    <td style={{ ...estiloNum, color: Math.abs(p.diferencia) >= 0.01 ? "#c62828" : "#2e7d32", fontWeight: "bold" }}>{dinero(p.diferencia)}</td>
                    <td style={{ ...estiloNum, color: "#666" }}>{p.porConfirmar ? dinero(p.porConfirmar) : "—"}</td>
                    <td style={estiloNum}>
                      {p.casos > 0 ? <button onClick={() => { setFiltroPlantel(p.oficial); setFiltroTipo(""); }} className="btn-select" aria-label={`Ver casos del plantel ${p.oficial}`} style={{ ...estiloBotonSecundario, padding: "2px 8px" }}>{p.casos}</button> : 0}
                    </td>
                    <td style={estiloTd}>
                      {p.casos > 0 && p.oficial && <button onClick={() => descargarPlantel(p.oficial)} className="btn-select" aria-label={`Descargar casos del plantel ${p.oficial}`} style={{ ...estiloBotonSecundario, padding: "2px 8px" }}>Excel</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {r.validacion && r.validacion.error && <Aviso tipo="error">No se validó con el banco: {r.validacion.error}</Aviso>}
          {r.validacion && !r.validacion.error && (
            <Aviso tipo="ok">
              Validación con el banco: <b>{r.validacion.validadas}</b> pago(s) validado(s) ({dinero(r.validacion.importe || 0)}) y <b>{r.validacion.confirmadas}</b> validación(es) del plantel confirmadas.
              {r.validacion.omitidas && r.validacion.omitidas.length > 0 && (
                <details style={{ marginTop: "4px" }}>
                  <summary style={{ cursor: "pointer" }}>{r.validacion.omitidas.length} no se validaron (quedan como caso)</summary>
                  <ul style={{ margin: "4px 0 0 0", paddingLeft: "18px" }}>{r.validacion.omitidas.slice(0, 50).map((x, i) => <li key={`${x.orden}-${i}`}>Orden {x.orden}: {x.motivo}</li>)}</ul>
                </details>
              )}
            </Aviso>
          )}
          {(r.validarConBanco.validar.length > 0 || r.validarConBanco.confirmar.length > 0) && (
            <div style={{ ...estiloTarjeta, backgroundColor: "#f8f9fa", marginBottom: "12px", display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: "0.9rem" }}>
                <b>{r.validarConBanco.validar.length}</b> pago(s) coinciden exactamente con una orden pendiente y <b>{r.validarConBanco.confirmar.length}</b> validación(es) del plantel cuadran con el banco.
              </span>
              <button onClick={validarAhora} disabled={consultando} className="btn-select validar" style={{ border: "none", padding: "8px 16px" }}>Validar con el banco</button>
            </div>
          )}
          {r.seguimiento && r.seguimiento.confirmados > 0 && <Aviso tipo="ok">{r.seguimiento.confirmados} caso(s) publicado(s) quedaron <b>confirmados con el banco</b>: el cruce de hoy ya no los marca.</Aviso>}
          {r.seguimiento && r.seguimiento.error && <Aviso tipo="error">{r.seguimiento.error}</Aviso>}
          {r.casos.some((c) => TIPOS_CASO[c.tipo].quien === "PLANTEL" && c.oficial) && (
            <div style={{ ...estiloTarjeta, backgroundColor: "#f8f9fa", marginBottom: "18px" }}>
              <h4 className="titulo-seccion" style={{ marginTop: 0 }}>3. Publicar casos a los planteles</h4>
              <p style={{ fontSize: "0.85rem", color: "#555", margin: "0 0 8px 0" }}>
                Los cajeros los verán en <b>Casos por corregir</b> y tendrán 5 días hábiles. Los casos que ya están publicados y abiertos no se repiten;
                los de pagos en línea no se publican (los corrige Sistemas).
              </p>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
                <select aria-label="Plantel a publicar" value={plantelPublicar} onChange={(e) => setPlantelPublicar(e.target.value)} style={{ ...estiloInput, padding: "6px" }}>
                  <option value="">Todos los planteles ({plantelesConNuevos.length})</option>
                  {plantelesConNuevos.map((p) => (
                    <option key={p.oficial} value={p.oficial}>{p.oficial} {p.nombre} ({casosPublicables(r.casos, p.oficial).filter((c) => !yaPublicado(c)).length})</option>
                  ))}
                </select>
                <button onClick={() => publicar(porPublicar)} disabled={!porPublicar.length || publicando} className="btn-select validar"
                  style={{ border: "none", padding: "8px 16px", opacity: (!porPublicar.length || publicando) ? 0.45 : 1, cursor: (!porPublicar.length || publicando) ? "not-allowed" : "pointer" }}>
                  {publicando ? "Publicando..." : `Publicar ${porPublicar.length} caso(s)`}
                </button>
                {!porPublicar.length && <span style={{ fontSize: "0.8rem", color: "#666" }}>No hay casos nuevos por publicar.</span>}
              </div>
            </div>
          )}

          {r.casos.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <h4 className="titulo-seccion">Casos ({casosVisibles.length}{casosVisibles.length !== r.casos.length ? ` de ${r.casos.length}` : ""})</h4>
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", margin: "6px 0 10px 0" }}>
                <select aria-label="Filtrar por plantel" value={filtroPlantel} onChange={(e) => setFiltroPlantel(e.target.value)} style={{ ...estiloInput, padding: "6px" }}>
                  <option value="">Todos los planteles</option>
                  {r.planteles.filter((p) => p.casos > 0).map((p) => <option key={p.oficial || "sin"} value={p.oficial}>{p.oficial} {p.nombre} ({p.casos})</option>)}
                </select>
                <select aria-label="Filtrar por tipo de caso" value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)} style={{ ...estiloInput, padding: "6px" }}>
                  <option value="">Todos los tipos</option>
                  {tiposPresentes.map((t) => <option key={t} value={t}>{TIPOS_CASO[t].titulo}</option>)}
                </select>
              </div>
              <table style={{ borderCollapse: "collapse", width: "100%" }}>
                <thead><tr>{["Plantel", "Caso", "Fecha", "Importe", "Banco", "Orden", "Cajero", "Qué hacer"].map((t) => <th key={t} style={estiloTh}>{t}</th>)}</tr></thead>
                <tbody>
                  {casosVisibles.slice(0, FILAS_VISIBLES).map((c, i) => (
                    <tr key={`${c.tipo}-${c.fecha}-${c.orden}-${c.autorizacion}-${c.folioBanco}-${i}`}>
                      <td style={estiloTd}>{c.oficial}</td>
                      <td style={estiloTd}>
                        {TIPOS_CASO[c.tipo].titulo}
                        {yaPublicado(c) && <div style={{ fontSize: "0.75rem", color: "#2e7d32" }}>Publicado al plantel</div>}
                        {c.automatico && <div style={{ fontSize: "0.75rem", color: "#003b5c" }}>Coincide exactamente: se valida con el banco</div>}
                        {c.seConfirma && <div style={{ fontSize: "0.75rem", color: "#003b5c" }}>Las partes suman la orden: se confirma con el banco</div>}
                        {c.probable && <div style={{ fontSize: "0.75rem", color: "#7a5c00" }}>Probable: {c.nota}</div>}
                        {!c.probable && c.nota && <div style={{ fontSize: "0.75rem", color: "#666" }}>{c.nota}</div>}
                      </td>
                      <td style={estiloTd}>{fechaCorta(c.fecha)} {c.hora}</td>
                      <td style={estiloNum}>{dinero(c.importe)}</td>
                      <td style={estiloTd}>
                        {c.medioCobro || ETIQUETAS_FUENTE[c.fuente]}
                        {c.autorizacion && <div style={{ fontSize: "0.75rem" }}>Aut. {c.autorizacion}{c.tecleado ? ` · tecleado ${c.tecleado}` : ""}</div>}
                        {!c.autorizacion && c.folioBanco && <div style={{ fontSize: "0.75rem" }}>Folio {c.folioBanco}</div>}
                      </td>
                      <td style={estiloTd}>
                        {c.orden ? <>{c.orden}<div style={{ fontSize: "0.75rem", color: "#666" }}>{c.estatus}{c.metodo ? ` · ${c.metodo}` : ""}{c.capturado ? ` · capturó ${c.capturado}` : ""}</div></> : "—"}
                      </td>
                      <td style={estiloTd}>{c.cajero}</td>
                      <td style={estiloTd}>{c.accion}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {casosVisibles.length > FILAS_VISIBLES && <p style={{ fontSize: "0.8rem", color: "#666" }}>Se muestran los primeros {FILAS_VISIBLES}; los archivos de Excel traen todos.</p>}
            </div>
          )}
          {r.casos.length === 0 && <Aviso tipo="ok">Todo cuadra en lo revisado: no hay casos por corregir.</Aviso>}

          {r.cargas.length > 0 && (
            <details style={{ marginTop: "16px", fontSize: "0.85rem" }}>
              <summary style={{ cursor: "pointer", color: "#003b5c" }}>Últimos archivos cargados</summary>
              <table style={{ borderCollapse: "collapse", width: "100%", marginTop: "6px" }}>
                <thead><tr>{["Cargado", "Por", "Tipo", "Archivo", "Fechas", "Movimientos", "Nuevos"].map((t) => <th key={t} style={estiloTh}>{t}</th>)}</tr></thead>
                <tbody>
                  {r.cargas.map((c) => (
                    <tr key={c.id}>
                      <td style={estiloTd}>{String(c.fecha_carga || "").substring(0, 16)}</td>
                      <td style={estiloTd}>{c.usuario}</td>
                      <td style={estiloTd}>{FUENTES[c.fuente] || c.fuente}</td>
                      <td style={estiloTd}>{c.nombre_archivo}</td>
                      <td style={estiloTd}>{fechaCorta(c.fecha_desde)} a {fechaCorta(c.fecha_hasta)}</td>
                      <td style={estiloNum}>{c.movimientos}</td>
                      <td style={estiloNum}>{c.nuevos}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

export default VistaConciliacionBancaria;
